/*!
 * Almagrove Jewellers — Shopify theme
 * Handcrafted by Sector 7 · https://sector7hq.co
 * © 2026 Sector7HQ Ltd. All rights reserved.
 *
 * alm-quick-view.js — quick-view modal driver.
 *   - Opens from any [data-alm-quickview-open][data-product-handle] click
 *   - Fetches /products/<handle>.js, builds a mini-PDP inside the modal
 *   - Variant picker as buttons (sold-out variants disabled)
 *   - Add to Bag posts to /cart/add — caught by the existing intercept in
 *     alm-cart-drawer.js, so the cart drawer auto-opens after success
 *   - Esc / overlay / × close, focus trap, body scroll lock
 */
(function () {
  'use strict';

  if (window.__almQVBound) return;
  window.__almQVBound = true;

  var modal, panel, body;
  var lastFocused = null;
  var cache = {};
  var current = null;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try { return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency }); }
    catch (e) { return '£' + (cents / 100).toFixed(2); }
  }

  function sized(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.toString();
    } catch (e) { return url; }
  }

  // ---------- open / close ----------
  function open(handle) {
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.body.classList.add('alm-qv-open');
    if (panel) setTimeout(function () { panel.focus(); }, 60);
    loadProduct(handle);
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('alm-qv-open');
    setTimeout(function () { modal.setAttribute('hidden', ''); }, 240);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    current = null;
  }

  // ---------- fetch + render ----------
  function loadProduct(handle) {
    if (cache[handle]) return render(cache[handle]);
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        cache[handle] = p;
        render(p);
      })
      .catch(function () { /* swallow */ });
  }

  function findVariant(product, picked) {
    // picked is array of option values [opt1, opt2, opt3]
    return product.variants.find(function (v) {
      for (var i = 0; i < picked.length; i++) {
        if (v.options[i] !== picked[i]) return false;
      }
      return true;
    });
  }

  function render(product) {
    if (!body) return;
    current = product;
    var media = product.media || [];
    var variants = product.variants || [];
    var initialVariant = variants.find(function (v) { return v.available; }) || variants[0];

    // Build option pickers
    var picked = initialVariant ? initialVariant.options.slice() : [];
    var optionsHtml = (product.options || []).map(function (opt, i) {
      var name = (typeof opt === 'object') ? opt.name : opt;
      var values = (typeof opt === 'object' && opt.values) ? opt.values : valuesForOption(product, i);
      return (
        '<div class="alm-qv__option" data-option-index="' + i + '">' +
          '<span class="alm-qv__option-label">' + escape(name) + '</span>' +
          '<div class="alm-qv__option-values">' +
            values.map(function (val) {
              var selected = picked[i] === val;
              return '<button type="button" class="alm-qv__option-btn' + (selected ? ' is-selected' : '') +
                     '" data-option-value="' + escape(val) + '">' + escape(val) + '</button>';
            }).join('') +
          '</div>' +
        '</div>'
      );
    }).join('');

    var img = (initialVariant && initialVariant.featured_image && initialVariant.featured_image.src)
      || (media[0] && media[0].src)
      || product.featured_image
      || '';

    var prices = priceHtml(product, initialVariant);

    body.innerHTML =
      '<div class="alm-qv__media">' +
        (img ? '<img data-alm-qv-image src="' + sized(img, 1200) + '" alt="' + escape(product.title) + '">' : '') +
        (media.length > 1 ? mediaThumbsHtml(media) : '') +
      '</div>' +
      '<div class="alm-qv__info">' +
        (product.vendor ? '<p class="alm-qv__vendor">' + escape(product.vendor) + '</p>' : '') +
        '<h2 class="alm-qv__title" id="alm-qv-title">' + escape(product.title) + '</h2>' +
        '<div data-alm-qv-prices>' + prices + '</div>' +
        (product.description ? '<div class="alm-qv__desc">' + product.description + '</div>' : '') +
        optionsHtml +
        '<div class="alm-qv__actions">' +
          '<div class="alm-qv__qty">' +
            '<button type="button" class="alm-qv__qty-btn" data-alm-qv-qty="-1" aria-label="Decrease">−</button>' +
            '<input class="alm-qv__qty-input" type="number" value="1" min="1" data-alm-qv-qty-input aria-label="Quantity">' +
            '<button type="button" class="alm-qv__qty-btn" data-alm-qv-qty="1" aria-label="Increase">+</button>' +
          '</div>' +
          '<form action="/cart/add" method="post" data-alm-qv-form>' +
            '<input type="hidden" name="id" value="' + (initialVariant ? initialVariant.id : '') + '" data-alm-qv-variant-id>' +
            '<input type="hidden" name="quantity" value="1" data-alm-qv-form-qty>' +
            '<button type="submit" class="alm-qv__submit" data-alm-qv-submit' +
              (initialVariant && initialVariant.available ? '' : ' disabled') + '>' +
              (initialVariant && initialVariant.available ? 'Add to Bag' : 'Sold Out') +
            '</button>' +
          '</form>' +
        '</div>' +
        '<a href="' + product.url + '" class="alm-qv__view-full">View full details →</a>' +
      '</div>';

    bindInteractions();
  }

  function valuesForOption(product, index) {
    var seen = {};
    var values = [];
    product.variants.forEach(function (v) {
      var val = v.options[index];
      if (val && !seen[val]) { seen[val] = true; values.push(val); }
    });
    return values;
  }

  function priceHtml(product, variant) {
    var price = variant ? variant.price : product.price;
    var compare = variant ? variant.compare_at_price : product.compare_at_price;
    if (compare && compare > price) {
      return (
        '<div class="alm-qv__prices">' +
          '<span class="alm-qv__price--compare">' + formatMoney(compare) + '</span>' +
          '<span class="alm-qv__price--sale">' + formatMoney(price) + '</span>' +
        '</div>'
      );
    }
    return '<div class="alm-qv__prices"><span>' + formatMoney(price) + '</span></div>';
  }

  function mediaThumbsHtml(media) {
    return (
      '<div class="alm-qv__media-thumbs" role="tablist">' +
        media.slice(0, 6).map(function (m, i) {
          return '<button type="button" class="alm-qv__media-thumb' + (i === 0 ? ' is-active' : '') +
                 '" data-alm-qv-thumb="' + i + '" aria-label="Show image ' + (i + 1) + '"></button>';
        }).join('') +
      '</div>'
    );
  }

  function bindInteractions() {
    if (!body) return;

    // Option picker
    body.addEventListener('click', function (ev) {
      var btn = ev.target.closest('.alm-qv__option-btn');
      if (btn) {
        var optWrap = btn.closest('[data-option-index]');
        var idx = Number(optWrap.getAttribute('data-option-index'));
        $$('.alm-qv__option-btn', optWrap).forEach(function (b) { b.classList.remove('is-selected'); });
        btn.classList.add('is-selected');
        // Recompute current variant from all selections
        var picked = $$('[data-option-index]', body).map(function (w) {
          var sel = w.querySelector('.alm-qv__option-btn.is-selected');
          return sel ? sel.getAttribute('data-option-value') : null;
        });
        var v = findVariant(current, picked);
        var hidden = $('[data-alm-qv-variant-id]', body);
        var submit = $('[data-alm-qv-submit]', body);
        var img = $('[data-alm-qv-image]', body);
        var pricesEl = $('[data-alm-qv-prices]', body);
        if (v) {
          if (hidden) hidden.value = v.id;
          if (submit) {
            submit.disabled = !v.available;
            submit.textContent = v.available ? 'Add to Bag' : 'Sold Out';
          }
          if (img && v.featured_image && v.featured_image.src) {
            img.src = sized(v.featured_image.src, 1200);
          }
          if (pricesEl) pricesEl.innerHTML = priceHtml(current, v);
        } else if (submit) {
          submit.disabled = true;
          submit.textContent = 'Unavailable';
        }
        return;
      }
      // Quantity
      var qBtn = ev.target.closest('[data-alm-qv-qty]');
      if (qBtn) {
        var step = Number(qBtn.getAttribute('data-alm-qv-qty'));
        var input = $('[data-alm-qv-qty-input]', body);
        var formQty = $('[data-alm-qv-form-qty]', body);
        if (input) {
          var v = Math.max(1, (parseInt(input.value, 10) || 1) + step);
          input.value = String(v);
          if (formQty) formQty.value = String(v);
        }
        return;
      }
      // Thumb
      var thumb = ev.target.closest('[data-alm-qv-thumb]');
      if (thumb) {
        var idx2 = Number(thumb.getAttribute('data-alm-qv-thumb'));
        $$('.alm-qv__media-thumb', body).forEach(function (b, i) { b.classList.toggle('is-active', i === idx2); });
        if (current && current.media && current.media[idx2]) {
          var img2 = $('[data-alm-qv-image]', body);
          if (img2) img2.src = sized(current.media[idx2].src, 1200);
        }
        return;
      }
    });

    // Sync hidden form qty when typing in the qty input
    var input = $('[data-alm-qv-qty-input]', body);
    var formQty = $('[data-alm-qv-form-qty]', body);
    if (input && formQty) {
      input.addEventListener('input', function () {
        var v = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = String(v);
        formQty.value = String(v);
      });
    }
  }

  // ---------- delegated triggers ----------
  document.addEventListener('click', function (ev) {
    var trigger = ev.target.closest('[data-alm-quickview-open][data-product-handle]');
    if (trigger) {
      ev.preventDefault();
      open(trigger.getAttribute('data-product-handle'));
      return;
    }
    if (ev.target.closest('[data-alm-quickview-close]')) {
      ev.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && modal && modal.classList.contains('is-open')) close();
  });

  function init() {
    modal = $('[data-alm-quickview]');
    if (!modal) return;
    panel = $('.alm-qv__panel', modal);
    body = $('[data-alm-qv-body]', modal);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
